import { useState } from "react"

import { useTranslation } from "react-i18next"

import type { HostedAgentEvalCaseDraft } from "../utils/hosted-agent-eval-cases"
import type { HostedAgentEvalCase } from "@/api/admin-api"

import { getHostedAgentEvalError, MAX_EVAL_CASES_PER_AGENT } from "@/api/admin-api"
import { ChevronDown, Plus, Trash2, TriangleAlert } from "@/components/icons"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Spinner } from "@/components/ui/spinner"

import { HostedAgentEvalCaseEditor } from "./hosted-agent-eval-case-editor"
import { HostedAgentEvalKindLabel } from "./hosted-agent-eval-outcome-badge"
import {
  useCreateHostedAgentEvalCaseMutation,
  useDeleteHostedAgentEvalCaseMutation,
  useUpdateHostedAgentEvalCaseMutation,
} from "../hooks/admin-queries"
import {
  createBlankHostedAgentEvalCase,
  toHostedAgentEvalCaseDraft,
  toHostedAgentEvalCaseInput,
  validateHostedAgentEvalCase,
} from "../utils/hosted-agent-eval-cases"

// The exam: CRUD over the agent's eval cases (#3028). Cases are persistent
// objects an admin edits without running anything, which is why they live in
// their own tab rather than behind the one-shot Test button.
//
// Selection here drives a SUBSET run: picking cases stamps the run `partial`,
// which is not comparable with a full-suite run — the run panel says so.

type CasesPanelProps = {
  agentId: string
  cases: HostedAgentEvalCase[]
  isLoading: boolean
  isError: boolean
  selectedCaseIds: string[]
  onToggleCase: (caseId: string) => void
  onClearSelection: () => void
}

export function HostedAgentEvalCasesPanel({
  agentId,
  cases,
  isLoading,
  isError,
  selectedCaseIds,
  onToggleCase,
  onClearSelection,
}: CasesPanelProps) {
  const { t } = useTranslation()
  const [draft, setDraft] = useState<HostedAgentEvalCaseDraft | null>(null)
  const [showErrors, setShowErrors] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const createMutation = useCreateHostedAgentEvalCaseMutation()
  const updateMutation = useUpdateHostedAgentEvalCaseMutation()
  const deleteMutation = useDeleteHostedAgentEvalCaseMutation()
  const isSaving = createMutation.isPending || updateMutation.isPending

  const atCap = cases.length >= MAX_EVAL_CASES_PER_AGENT
  const validation = draft
    ? validateHostedAgentEvalCase(draft)
    : { case: [], expectations: {} as Record<string, never> }

  const openNew = () => {
    setDraft(createBlankHostedAgentEvalCase())
    setShowErrors(false)
    setSaveError(null)
  }

  const openExisting = (source: HostedAgentEvalCase) => {
    setDraft(toHostedAgentEvalCaseDraft(source))
    setShowErrors(false)
    setSaveError(null)
  }

  const closeEditor = () => {
    setDraft(null)
    setShowErrors(false)
    setSaveError(null)
  }

  const handleSave = async () => {
    if (!draft) return
    if (validateHostedAgentEvalCase(draft).case.length > 0) {
      setShowErrors(true)
      return
    }
    setSaveError(null)
    const input = toHostedAgentEvalCaseInput(draft)
    try {
      if (draft.id) {
        await updateMutation.mutateAsync({ agentId, caseId: draft.id, input })
      } else {
        await createMutation.mutateAsync({ agentId, input })
      }
      closeEditor()
    } catch (error) {
      const evalError = getHostedAgentEvalError(error)
      if (evalError?.code === "eval_case_conflict") {
        setSaveError(
          t("adminDashboard.hostedAgents.evals.cases.conflict", {
            defaultValue:
              "The case could not be saved — either the 25-case limit is reached or someone else changed the cases. Reload and try again.",
          })
        )
      } else if (evalError?.code === "invalid_eval_case") {
        setSaveError(
          t("adminDashboard.hostedAgents.evals.cases.invalid", {
            defaultValue: "The server rejected this case: {{detail}}",
            detail: evalError.message,
          })
        )
      } else {
        setSaveError(
          evalError?.message ??
            (error instanceof Error
              ? error.message
              : t("adminDashboard.hostedAgents.evals.cases.saveError", {
                  defaultValue: "The case could not be saved.",
                }))
        )
      }
    }
  }

  const handleDelete = async (caseId: string) => {
    try {
      await deleteMutation.mutateAsync({ agentId, caseId })
      if (selectedCaseIds.includes(caseId)) onToggleCase(caseId)
      if (draft?.id === caseId) closeEditor()
    } catch {
      setSaveError(
        t("adminDashboard.hostedAgents.evals.cases.deleteError", {
          defaultValue: "The case could not be deleted.",
        })
      )
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-medium">
            {t("adminDashboard.hostedAgents.evals.cases.title", { defaultValue: "Test cases" })}
          </p>
          <p className="text-muted-foreground text-xs">
            {t("adminDashboard.hostedAgents.evals.cases.count", {
              defaultValue: "{{used}} of {{max}} cases",
              used: cases.length,
              max: MAX_EVAL_CASES_PER_AGENT,
            })}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {selectedCaseIds.length > 0 ? (
            <Button size="sm" type="button" variant="ghost" onClick={onClearSelection}>
              {t("adminDashboard.hostedAgents.evals.cases.clearSelection", {
                defaultValue: "Clear selection",
              })}
            </Button>
          ) : null}
          <Button
            disabled={atCap || !!draft}
            size="sm"
            type="button"
            variant="outline"
            onClick={openNew}
          >
            <Plus className="size-4" />
            {t("adminDashboard.hostedAgents.evals.cases.add", { defaultValue: "Add case" })}
          </Button>
        </div>
      </div>

      {atCap ? (
        <Alert>
          <TriangleAlert className="text-foreground size-4" />
          <AlertTitle>
            {t("adminDashboard.hostedAgents.evals.cases.capTitle", {
              defaultValue: "Case limit reached",
            })}
          </AlertTitle>
          <AlertDescription>
            {t("adminDashboard.hostedAgents.evals.cases.capBody", {
              defaultValue:
                "An agent can hold {{max}} eval cases. Delete one before adding another.",
              max: MAX_EVAL_CASES_PER_AGENT,
            })}
          </AlertDescription>
        </Alert>
      ) : null}

      {saveError ? (
        <p className="bg-destructive text-destructive-foreground border-foreground rounded-md border px-2 py-1 text-xs">
          {saveError}
        </p>
      ) : null}

      {draft ? (
        <div className="space-y-3 rounded-lg border p-3">
          <p className="text-sm font-medium">
            {draft.id
              ? t("adminDashboard.hostedAgents.evals.cases.editTitle", {
                  defaultValue: "Edit case",
                })
              : t("adminDashboard.hostedAgents.evals.cases.newTitle", {
                  defaultValue: "New case",
                })}
          </p>
          <HostedAgentEvalCaseEditor
            draft={draft}
            showErrors={showErrors}
            validation={validation}
            onChange={(changes) => setDraft({ ...draft, ...changes })}
          />
          <div className="flex flex-wrap gap-2">
            <Button disabled={isSaving} type="button" onClick={() => void handleSave()}>
              {isSaving ? <Spinner className="size-4" /> : null}
              {t("adminDashboard.hostedAgents.evals.cases.save", { defaultValue: "Save case" })}
            </Button>
            <Button disabled={isSaving} type="button" variant="ghost" onClick={closeEditor}>
              {t("adminDashboard.hostedAgents.evals.cases.cancel", { defaultValue: "Cancel" })}
            </Button>
          </div>
        </div>
      ) : null}

      <CaseList
        cases={cases}
        isError={isError}
        isLoading={isLoading}
        selectedCaseIds={selectedCaseIds}
        onDelete={(caseId) => void handleDelete(caseId)}
        onEdit={openExisting}
        onToggleCase={onToggleCase}
      />
    </div>
  )
}

function CaseList({
  cases,
  isLoading,
  isError,
  selectedCaseIds,
  onToggleCase,
  onEdit,
  onDelete,
}: {
  cases: HostedAgentEvalCase[]
  isLoading: boolean
  isError: boolean
  selectedCaseIds: string[]
  onToggleCase: (caseId: string) => void
  onEdit: (source: HostedAgentEvalCase) => void
  onDelete: (caseId: string) => void
}) {
  const { t } = useTranslation()

  if (isLoading) {
    return (
      <p className="text-muted-foreground flex items-center gap-2 text-xs">
        <Spinner className="size-3" />
        {t("adminDashboard.hostedAgents.evals.cases.loading", { defaultValue: "Loading cases…" })}
      </p>
    )
  }
  if (isError) {
    return (
      <p className="text-muted-foreground text-xs">
        {t("adminDashboard.hostedAgents.evals.cases.loadError", {
          defaultValue: "The eval cases could not be loaded right now.",
        })}
      </p>
    )
  }
  if (cases.length === 0) {
    return (
      <p className="text-muted-foreground text-xs">
        {t("adminDashboard.hostedAgents.evals.cases.empty", {
          defaultValue:
            "No cases yet. A case is one fixed prompt plus what must be true about the answer.",
        })}
      </p>
    )
  }

  return (
    <ul className="space-y-2">
      {cases.map((source) => (
        <li key={source.id}>
          <CaseRow
            selected={selectedCaseIds.includes(source.id)}
            source={source}
            onDelete={() => onDelete(source.id)}
            onEdit={() => onEdit(source)}
            onToggle={() => onToggleCase(source.id)}
          />
        </li>
      ))}
    </ul>
  )
}

function CaseRow({
  source,
  selected,
  onToggle,
  onEdit,
  onDelete,
}: {
  source: HostedAgentEvalCase
  selected: boolean
  onToggle: () => void
  onEdit: () => void
  onDelete: () => void
}) {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)

  return (
    <div className="space-y-2 rounded-lg border p-3">
      <div className="flex flex-wrap items-center gap-2">
        {/* A disabled case is excluded server-side (`enabled_only`), so letting
            it be ticked would promise a run of N cases and deliver fewer. */}
        <Checkbox
          checked={selected}
          disabled={!source.enabled}
          aria-label={
            source.enabled
              ? t("adminDashboard.hostedAgents.evals.cases.selectAria", {
                  defaultValue: "Include {{name}} in the next run",
                  name: source.name,
                })
              : t("adminDashboard.hostedAgents.evals.cases.selectDisabledAria", {
                  defaultValue: "{{name}} is disabled and cannot be run",
                  name: source.name,
                })
          }
          onCheckedChange={onToggle}
        />
        <span className="flex-1 text-sm font-medium">{source.name}</span>
        {!source.enabled ? (
          <Badge variant="disabled">
            {t("adminDashboard.hostedAgents.evals.cases.disabled", { defaultValue: "Disabled" })}
          </Badge>
        ) : null}
        <Badge variant="secondary">
          {t("adminDashboard.hostedAgents.evals.cases.expectationCount", {
            defaultValue: "{{n}} expectations",
            n: source.expectations.length,
          })}
        </Badge>
        <Button
          size="icon"
          type="button"
          variant="ghost"
          aria-label={t("adminDashboard.hostedAgents.evals.cases.expandAria", {
            defaultValue: "Show what this case checks",
          })}
          onClick={() => setIsOpen((open) => !open)}
        >
          <ChevronDown className={`size-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
        </Button>
        <Button size="sm" type="button" variant="outline" onClick={onEdit}>
          {t("adminDashboard.hostedAgents.evals.cases.edit", { defaultValue: "Edit" })}
        </Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              size="icon"
              type="button"
              variant="ghost"
              aria-label={t("adminDashboard.hostedAgents.evals.cases.deleteAria", {
                defaultValue: "Delete case",
              })}
            >
              <Trash2 className="size-4" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t("adminDashboard.hostedAgents.evals.cases.deleteTitle", {
                  defaultValue: "Delete this case?",
                })}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t("adminDashboard.hostedAgents.evals.cases.deleteBody", {
                  defaultValue:
                    "Past runs keep their results, but future runs will no longer test this. Deleting a case also changes the test digest, so later runs are no longer directly comparable with earlier ones.",
                })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>
                {t("adminDashboard.hostedAgents.evals.cases.cancel", { defaultValue: "Cancel" })}
              </AlertDialogCancel>
              <AlertDialogAction onClick={onDelete}>
                {t("adminDashboard.hostedAgents.evals.cases.confirmDelete", {
                  defaultValue: "Delete",
                })}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {isOpen ? (
        <div className="space-y-2 border-t pt-2">
          <p className="text-muted-foreground text-xs whitespace-pre-wrap">{source.prompt}</p>
          <ul className="space-y-1">
            {source.expectations.map((expectation, index) => (
              <li key={`${source.id}-${index}`} className="text-xs">
                <span className="font-medium">
                  <HostedAgentEvalKindLabel kind={expectation.kind} />
                </span>
                {summarizeExpectation(expectation) ? (
                  <span className="text-muted-foreground">
                    {" "}
                    — {summarizeExpectation(expectation)}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  )
}

/** The operand an admin needs to read the rule, without the enum noise. */
function summarizeExpectation(expectation: HostedAgentEvalCase["expectations"][number]): string {
  if (expectation.expected) return expectation.expected
  if (expectation.value) return `"${expectation.value}"`
  if (expectation.tool) {
    const parts = [expectation.tool]
    if (expectation.action) parts.push(expectation.action)
    if (expectation.code) parts.push(expectation.code)
    return parts.join(" · ")
  }
  if (expectation.limit != null) return String(expectation.limit)
  if (expectation.min_count != null) return `≥ ${expectation.min_count}`
  return ""
}
